import React, { useCallback, useContext, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';
import * as Yup from 'yup';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { Grid, Typography, Button, List, ListItem } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import ClearIcon from '@material-ui/icons/Clear';

import { AisOrder, ArchivalUnitReference } from 'types';
import { ReworkCtx } from 'components/rework-ctx/rework-ctx';
import { FormItemTextField } from 'components/form-item/form-item-text-field';
import { NotificationCtx } from 'components/notification/notification-hook';
import { NotificationVariant } from 'components/notification/notification-types';
import { TextField } from 'components/text-field/text-field';
import { putReworkOrder } from './order-api';
import { AuthCtx } from '../../components/auth/auth-ctx';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    form: {
      color: 'black',
      marginBottom: 50,
    },
    link: {
      color: theme.palette.primary.main,
      marginRight: 15,
    },
    unitLink: {
      width: '100%',
      color: theme.palette.primary.main,
      display: 'flex',
      alignItems: 'center',
    },
    listItem: {
      padding: 5,
      '&:hover': {
        backgroundColor: 'rgba(207, 41, 49, 0.1)',
      },
    },
    listItemName: {
      flexGrow: 1,
    },
    iconButton: {
      width: 20,
      height: 20,
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
      },
      '& svg': {
        fontSize: '1rem',
      },
    },
    submitButton: {
      margin: '15px 0',
      marginRight: '15px',
    },
  })
);

function NOOP() {}

export function OrderRework() {
  const classes = useStyles();

  const history = useHistory();
  const { showNotification } = useContext(NotificationCtx);
  const { aisOrder, clearContext } = useContext(ReworkCtx);
  const { formatMessage } = useIntl();
  const { researchSheetToDisplayInOrder } = useContext(AuthCtx);

  if (!aisOrder) {
    history.push('/portfolio/historie-objednavek');
  }

  const initialValues: AisOrder = useMemo(() => {
    return {
      id: aisOrder?.id ?? '',
      created: aisOrder?.created,
      adminNote: aisOrder?.adminNote,
      description: aisOrder?.description,
      phone: aisOrder?.phone,
      fullName: aisOrder?.fullName,
      requestedForDate: aisOrder?.requestedForDate,
      archivalAidMetadata: aisOrder?.archivalAidMetadata,
      archivalCollection: aisOrder?.archivalCollection,
      archivalUnits: aisOrder?.archivalUnits,
      filesExpiration: aisOrder?.filesExpiration,
      files: aisOrder?.files ?? [],
      requestedAisIds: aisOrder?.requestedAisIds ?? [],
    };
  }, [aisOrder]);

  const handleRemoveItem = useCallback(
    (props: FormikProps<AisOrder>, unit: ArchivalUnitReference) => (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
      const { values, setFieldValue } = props;
      const { archivalUnits } = values;

      e.preventDefault();

      setFieldValue(
        'archivalUnits',
        archivalUnits?.filter(({ id }) => id !== unit?.id)
      );
    },
    []
  );

  const handleRemoveAisObject = useCallback(
    (props: FormikProps<AisOrder>) => () => {
      const { setFieldValue } = props;

      setFieldValue('archivalAidMetadata', undefined);
      setFieldValue('archivalCollection', undefined);
      clearContext?.();
    },
    [clearContext]
  );

  const handleSubmit = useCallback(
    async (values: AisOrder, actions: FormikHelpers<AisOrder>) => {
      try {
        const response = await putReworkOrder(values);

        if (response) {
          showNotification(
            formatMessage({ id: 'reworkOrder.successfullySent' }),
            NotificationVariant.SUCCESS
          );

          clearContext?.();

          actions.resetForm();
          history.push('/portfolio/historie-objednavek');
        } else {
          throw new Error('Status not ok');
        }
      } catch (e) {
        showNotification(
          formatMessage({ id: 'reworkOrder.failedToSend' }),
          NotificationVariant.ERROR
        );
      }

      actions.setSubmitting(false);
    },
    [clearContext, formatMessage, history, showNotification]
  );

  return (
    <Grid container className={classes.root}>
      <Grid item xs={10} md={6}>
        <div className={classes.form}>
          <Formik<AisOrder>
            initialValues={initialValues}
            enableReinitialize={true}
            onSubmit={handleSubmit}
            validateOnChange={false}
            validateOnBlur={false}
            validateOnMount={false}
            validationSchema={Yup.object({
              description: Yup.mixed().test('Popis', '', function (value) {
                const newOrder =
                  (this.options as { parent: AisOrder }).parent || {};

                const {
                  archivalAidMetadata,
                  archivalCollection,
                  description = '',
                } = newOrder;

                const withAidObject =
                  !!archivalAidMetadata?.id || !!archivalCollection?.id;
                const shortDescription = description.length < 5;

                if (withAidObject && shortDescription) {
                  return this.createError({
                    path: 'description',
                    message:
                      'Předmět objednávky musí obsahovat nejméně 5 znaků',
                  });
                }

                return false;
              }),
            })}
          >
            {(props) => {
              const { handleSubmit, values } = props;

              const orderVersion = !!values?.archivalUnits?.length
                ? 'archival-units'
                : !!values?.archivalAidMetadata
                ? 'archival-aid'
                : !!values?.archivalCollection
                ? 'archival-collection'
                : 'generic';

              const showGeneric = orderVersion === 'generic';
              const showAid = orderVersion === 'archival-aid';
              const showCollection = orderVersion === 'archival-collection';
              const showUnits = orderVersion === 'archival-units';

              return (
                <form onSubmit={handleSubmit}>
                  <Typography variant="body1" align="left">
                    Přepracujte objednávku dle poznámky správce:
                  </Typography>
                  <FormItemTextField
                    name="adminNote"
                    label={formatMessage({ id: 'reworkOrder.adminNote' })}
                    disabled={true}
                    multiline={true}
                  />
                  <FormItemTextField
                    name="fullName"
                    label={formatMessage({ id: 'aisOrder.fullName' })}
                    disabled={true}
                  />
                  <TextField
                    name="researchSheet.ordinalNumber"
                    label={formatMessage({
                      id: 'aisOrder.currentlyUsedSheetOrdinalNumber',
                    })}
                    disabled={true}
                    value={researchSheetToDisplayInOrder ?? ''}
                    onChange={NOOP}
                    formControlStyles={{ marginTop: 28 }}
                  />
                  <FormItemTextField
                    name="phone"
                    type="tel"
                    disabled={true}
                    label={formatMessage({
                      id: 'aisOrder.phone',
                      defaultMessage: 'Telefonní číslo',
                    })}
                  />

                  {showAid && (
                    <>
                      <TextField
                        name="archivalAid"
                        label="Archivní pomůcka"
                        value={`${values?.archivalAidMetadata?.evidenceNumber} - ${values?.archivalAidMetadata?.name}`}
                        onChange={NOOP}
                        disabled={true}
                        formControlStyles={{ marginTop: 28 }}
                      />
                      <Button
                        variant="contained"
                        className={classes.submitButton}
                        onClick={handleRemoveAisObject(props)}
                      >
                        <Typography component="span" variant="body2">
                          {formatMessage({ id: 'aisOrder.removeAidButton' })}
                        </Typography>
                      </Button>
                    </>
                  )}

                  {showCollection && (
                    <>
                      <TextField
                        name="archivalCollection"
                        label="Archivní fond"
                        value={`${values?.archivalCollection?.evidenceNumber} - ${values?.archivalCollection?.name}`}
                        onChange={NOOP}
                        disabled={true}
                        formControlStyles={{ marginTop: 28 }}
                      />
                      <Button
                        variant="contained"
                        className={classes.submitButton}
                        onClick={handleRemoveAisObject(props)}
                      >
                        <Typography component="span" variant="body2">
                          {formatMessage({
                            id: 'aisOrder.removeCollectionButton',
                          })}
                        </Typography>
                      </Button>
                    </>
                  )}

                  {showUnits && (
                    <List
                      component="div"
                      disablePadding
                      dense={true}
                      subheader={
                        <Typography component="h6" variant="h6">
                          Archiválie k objednání
                        </Typography>
                      }
                    >
                      {!!values?.archivalUnits?.length ? (
                        values?.archivalUnits?.map((unit) => (
                          <ListItem key={unit.id} className={classes.listItem}>
                            <Typography
                              className={classes.listItemName}
                              component="span"
                            >
                              {unit?.numericId}
                            </Typography>
                            <IconButton
                              size="small"
                              className={classes.iconButton}
                              onClick={handleRemoveItem(props, unit)}
                            >
                              <ClearIcon />
                            </IconButton>
                          </ListItem>
                        ))
                      ) : (
                        <Typography component="span">
                          Žádné archivalie k objednání
                        </Typography>
                      )}
                    </List>
                  )}

                  <FormItemTextField
                    name="description"
                    label={formatMessage({
                      id: showGeneric
                        ? 'aisOrder.description'
                        : showUnits
                        ? 'aisOrder.descriptionUnits'
                        : 'aisOrder.descriptionAidAndCollection',
                    })}
                    multiline={true}
                  />
                  <FormItemTextField
                    name="requestedForDate"
                    type="date"
                    label={formatMessage({ id: 'aisOrder.requestedForDate' })}
                  />

                  <Typography variant="body1" style={{ marginTop: 15 }}>
                    {formatMessage({ id: 'aisOrder.readyInfo' })}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    className={classes.submitButton}
                  >
                    <Typography component="span" variant="body2">
                      {formatMessage({ id: 'reworkOrder.submitButton' })}
                    </Typography>
                  </Button>
                </form>
              );
            }}
          </Formik>
        </div>
      </Grid>
    </Grid>
  );
}
